import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { mdiEmoticonSadOutline, mdiListBoxOutline } from "@mdi/js";
import { Box, Button, Typography } from "@mui/material";
import Icon from "@mdi/react";

import SecretSantaApi, { GetMyPairWishlistRes, SECRET_SANTA_ACTIONS } from "api/secret-santa";
import useNotifier from "hooks/useNotifier";
import { GetUserById } from "api/profile";
import PageLoader from "components/PageLoader";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import AsyncAvatar from "components/AsyncAvatar";
import useBoundStore from "store";
import getNameInitials from "utils/getNameInitials";
import { StorageBuckets } from "api/storage";
import DateCountdown from "components/DateCountdown";
import CircularLoader from "components/CircularLoader";
import Wishlist from "../Wishlist";

export interface MyPairProps {
  eventId: number;
  groupId: number;
  isAppFocused: boolean;
  readonly?: boolean;
  userAuthId?: number;
  users: GetUserById[];
}

export default function MyPair(props: MyPairProps) {
  const { eventId, groupId, isAppFocused, readonly, userAuthId, users } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [secretSanta, setSecretSanta] = useState<GetMyPairWishlistRes | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const smallAvatars = useBoundStore((state) => state.miscellaneous.smallAvatars);
  const { showNotification } = useNotifier();
  const secretSantaApi = useMemo(() => new SecretSantaApi(showNotification), [showNotification]);
  const receiver = users.find((user) => user.uid === secretSanta?.receiver_id);
  const canRequestAgain = secretSanta?.requested_at
    ? dayjs().diff(dayjs(secretSanta?.requested_at), "days") >= 1
    : true;

  useEffect(() => {
    (async () => {
      if (userAuthId) {
        const { data } = await secretSantaApi.getMyPairWishlist(userAuthId, eventId);
        setSecretSanta(data);
        setIsLoading(false);
      }
    })();
  }, [isAppFocused, userAuthId]);

  async function handleRequestWishlist() {
    if (userAuthId) {
      setIsRequesting(true);
      const { error } = await secretSantaApi.requestWishlist(eventId, groupId, SECRET_SANTA_ACTIONS.REQUEST_WISHLIST);
      const { data } = await secretSantaApi.getMyPairWishlist(userAuthId, eventId);
      if (!error) {
        showNotification({
          message: "Request wishlist successfully sent.",
          severity: "success",
        });
      }
      setSecretSanta(data);
      setIsRequesting(false);
    }
  }

  if (isLoading) {
    return <PageLoader />;
  }

  if (!receiver || !secretSanta) {
    return (
      <DataIconPlaceholder
        icon={mdiEmoticonSadOutline}
        text='Something went wrong as your Secret Santa pair was not found'
      />
    );
  }

  return (
    <Box display='flex' flexDirection='column' alignItems='center'>
      <Box display='flex' flexDirection='column' alignItems='center' mt={2} mb={4}>
        <AsyncAvatar
          avatarFile={smallAvatars[receiver.avatar ?? ""]}
          avatarName={receiver.avatar}
          placeholder={getNameInitials(receiver.name)}
          bucket={StorageBuckets.PROFILES_AVATARS}
        />
        <Typography mt={1.5}>{receiver.name}&#39;s wishlist</Typography>
      </Box>
      {secretSanta.wishlist.length ? (
        <Box sx={{ width: "100%" }}>
          <Wishlist items={secretSanta.wishlist} />
        </Box>
      ) : null}

      {!secretSanta.wishlist.length && !readonly && (
        <Box display='flex' flexDirection='column' alignItems='center' maxWidth={350}>
          {canRequestAgain ? (
            <Typography align='center' color='text.secondary'>
              It appears your Secret Santa recipient has not completed their wishlist yet. Click on{" "}
              <b>Request wishlist</b> to discreetly send them a notification.
            </Typography>
          ) : (
            <Box display='flex' flexDirection='column' alignItems='center'>
              <Typography gutterBottom align='center' color='text.secondary'>
                Time remaining before you can request the wishlist again.
              </Typography>
              <DateCountdown date={dayjs(secretSanta.requested_at).add(1, "day").toString()} />
            </Box>
          )}

          <CircularLoader loading={isRequesting} sx={{ mt: 3 }}>
            <Button
              disabled={!canRequestAgain || isRequesting}
              variant='contained'
              startIcon={<Icon path={mdiListBoxOutline} size={1} />}
              onClick={handleRequestWishlist}
            >
              Request wishlist
            </Button>
          </CircularLoader>
        </Box>
      )}
    </Box>
  );
}
